import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { registrationActivityKeySchema } from '@entities/education-activity';
import { StudentEducationActivityPage } from '@pages/student';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessPersonalFlow } from '@shared/lib';

export const educationActivitySearchSchema = z.object({
  mode: z.enum(['resume', 'review']).catch('resume'),
  slideId: z.string().min(1).optional().catch(undefined),
});

export function enforceRegistrationEducationActivityRouteAccess({
  context,
  params,
}: {
  context: RouterContext;
  params: { locale: string; activityKey: string };
}): void {
  if (context.auth.isLoading) return;

  const hasMenuAccess = canAccessPersonalFlow(context.ability, context.auth.user?.id ?? null);
  if (hasMenuAccess && registrationActivityKeySchema.safeParse(params.activityKey).success) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({
    to: '/$locale/unauthorized',
    params: { locale: params.locale },
  });
}

export const Route = createFileRoute(
  '/$locale/_authenticated/student/registrations/$registrationId/activities/$activityKey'
)({
  component: RegistrationEducationActivityRoute,
  validateSearch: educationActivitySearchSchema,
  beforeLoad: ({ context, params }) => {
    enforceRegistrationEducationActivityRouteAccess({
      context: context as RouterContext,
      params,
    });
  },
});

function RegistrationEducationActivityRoute() {
  const { locale, registrationId, activityKey } = Route.useParams();
  const { mode, slideId } = Route.useSearch();

  return (
    <StudentEducationActivityPage
      locale={locale}
      context="registration"
      registrationId={registrationId}
      activityKey={activityKey}
      mode={mode}
      slideId={slideId}
    />
  );
}
